import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, ActivityIndicator, Image } from 'react-native';
import type { AthleteProps, BestAvailableOrderProps, EventOrderStatProps, LeagueProps, MarketProps, MatchProps, OrderProps, PollProps, TeamProps, TournamentProps, TradeProps } from '../../../types';
import { Icons, LinearDiagnal } from '../../../Components';
import { MarketComponentApi, MarketComponentHelpers } from '../../api';
import AthleteTournamentMarket from '../AthleteTournamentMarket';
import TeamTournamentMarket from '../TeamTournamentMarket';
import MatchMarket from '../MatchMarket';
import moment from 'moment-mini';
import { TournamentMarketHelpers } from './api';
import { useIsInViewport } from '../../../Components/ViewportObserver';
import { Button, Text, TextInput, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';


type TournamentMarketProps = {
    tournament:TournamentProps,
    league?:LeagueProps,
    markets:MarketProps[],
    hide_liquidity?:boolean,
    width?:number,
    highlight_router_orders?:boolean,
    style?:any,
    hide_match_liquidity?:boolean,
    show_grades?:boolean,
    float?:boolean,
    default_price_view:'last_trade'|'best_available',
    disabled_markets?:string[],
    hide_participant_liquidity?:boolean,
    activate_loading?:boolean,
    wrap_markets?:boolean,
    latest_trades:TradeProps[],
    best_available_orders:BestAvailableOrderProps[],
    match_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    match_order_stats:EventOrderStatProps[],
    match_latest_trades:TradeProps[],
    market_selector_location?:string,
    flash_markets?:PollProps[],
    onShare?:(t:TournamentProps) => void,
    onView:(data:{ event_id:string, event_type:string, market_id:string, side_type?:string, side_id?:string }) => void,
    onTradeLongPress:(trade:TradeProps) => void,
    onViewMarketStats?:() => void,
    onFlashSelect?:(poll_id:string) => void,
    onActivate?:(event_id:string, event_type:string) => void,
    onViewAdditionalMarkets: (event_id:string, event_type:string) => void,
    onOrder:(order:OrderProps) => void
}

const TournamentMarket = ({ tournament, default_price_view, highlight_router_orders, float, disabled_markets, wrap_markets, width, style, market_selector_location, flash_markets, hide_match_liquidity, show_grades, hide_participant_liquidity, league, markets, latest_trades, hide_liquidity, best_available_orders, event_order_stats, match_available_orders, match_latest_trades, match_order_stats, activate_loading, onView, onTradeLongPress, onViewAdditionalMarkets, onViewMarketStats, onFlashSelect, onOrder, onActivate, onShare }:TournamentMarketProps) => {
    const C = useColors();
    const [ search_value, setSearchValue ] = useState<string | undefined>(undefined);
    const [ tournament_data, setTournamentData ] = useState<{
        loaded:boolean,
        active_type:'futures'|'matches',
        loading:boolean,
        athletes:AthleteProps[],
        teams:TeamProps[],
        selected_flash?:string,
        matches:MatchProps[],
        match_trades:TradeProps[],
        active_market?:MarketProps
    }>({
        loading:false,
        loaded:false,
        active_type: 'futures',
        athletes:[],
        match_trades:[], 
        teams:[],
        matches:[]
    });
    const { loaded, athletes, selected_flash, active_type, loading, matches, teams, active_market, match_trades } = tournament_data;
    const [ show_markets, setShowMarkets ] = useState(false);
    const ref = useRef(null);
    const is_in_viewport = useIsInViewport(ref)

    let supported_markets = markets.filter(m => tournament.supported_markets.find(sm => sm.market_id == m.market_id));
    let all_supported_markets = supported_markets
    if(active_type == 'futures'){
        supported_markets = supported_markets.filter(sm => sm.event_type == 'tournament');
    } else {
        supported_markets = supported_markets.filter(sm => sm.event_type == 'match');
    }

    const { show_athletes, show_teams, show_matches, show_futures } = useMemo(() => TournamentMarketHelpers.visibleMarketTypes(all_supported_markets),[all_supported_markets.length])
    const { liquidity, open_order_count } = useMemo(() => MarketComponentHelpers.getLiqudity(event_order_stats.concat(match_order_stats.filter(m => matches.find(nm => nm.match_id == m.event_id)))),[JSON.stringify(event_order_stats)])
    const sorted_athletes = useMemo(() => TournamentMarketHelpers.sortAthletes(event_order_stats, latest_trades, athletes, active_market, search_value),[athletes.length, active_market?.market_id, JSON.stringify(event_order_stats), search_value])
    const sorted_teams = useMemo(() => TournamentMarketHelpers.sortTeams(event_order_stats, latest_trades, teams, active_market, search_value),[teams.length, active_market?.market_id, JSON.stringify(event_order_stats), search_value])
    let sorted_matches = useMemo(() => TournamentMarketHelpers.sortMatches(match_order_stats, matches, active_market, search_value, athletes, teams),[matches.length, JSON.stringify(match_order_stats), active_market?.market_id, search_value])
    if(!wrap_markets){ sorted_matches = sorted_matches.filter(m => !['closed','cancelled','postponed','suspended'].includes(m.status)) }
    const market_width = useMemo(() => TournamentMarketHelpers.getMarketWrapWidth(50, width, wrap_markets),[width, wrap_markets])
    const upcoming_flash_market = TournamentMarketHelpers.getUpcomingFlashMarket(tournament, matches, flash_markets, selected_flash);

    useEffect(() => {
        if(loaded || loading){ return }
        MarketComponentApi.setEnvironment();
        getTournamentData(tournament)
    },[is_in_viewport, tournament.tournament_id]);

    const getTournamentData = async(t:TournamentProps) => {
        if(loaded || loading){ return }
        setTournamentData({ ...tournament_data, loading:true });
        let t_ids:string[] = [];
        let a_ids:string[] = [];
        let mtchs:MatchProps[] = await MarketComponentApi.getMatchesByTournament(t.tournament_id);
        let trades = await MarketComponentApi.getLatestTradesByEvents('match', mtchs.map(m => m.match_id));
        mtchs.map(m => {
            if(t.participant_type == 'athlete'){ a_ids = a_ids.concat(m.participants) }
            if(t.participant_type == 'team'){ t_ids = t_ids.concat(m.participants) }
        })
        if(t.participant_type == 'team'){ t_ids = t_ids.concat(t.participants) }
        if(t.participant_type == 'athlete'){ a_ids = a_ids.concat(t.participants) }

        let sm = tournament.supported_markets[0]
        let market = markets.find(m => m.market_id == sm?.market_id);
        let type = 'futures'
        if(market?.event_type == 'match'){ type = 'matches' }
        const aths = await MarketComponentApi.getAthletesByIds(a_ids);
        const tms = await MarketComponentApi.getTeamsByIds(t_ids);
        setTournamentData({
            ...tournament_data,
            loading:false,
            loaded: true,
            active_type: type as 'futures' | 'matches',
            athletes: aths,
            match_trades: trades,
            teams: tms,
            matches: mtchs,
            active_market: market
        })
    }


    const renderMarkets = (data: { item:MarketProps, index:number }) => {
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:C.borders.light }} onPress={() => {
                setTournamentData({ ...tournament_data, active_market: data.item });
                setShowMarkets(false);
            }}>
                <Text size={12} theme='h2'>{data.item.type == 'Stat' ? data.item.stat_label : data.item.type}</Text>
            </Button>
        )
    }

    const renderMatches = (data: { item:MatchProps, index:number }) => {
        if(!active_market){ return <></> }
        let home_athlete = athletes.find(a => a.athlete_id == data.item.participants[1]);
        let away_athlete = athletes.find(a => a.athlete_id == data.item.participants[0]);
        let home_team = teams.find(a => a.team_id == data.item.participants[1]);
        let away_team = teams.find(a => a.team_id == data.item.participants[0]);

        let m_available_orders = match_available_orders.filter(ba => ba.event_id == data.item.match_id && ba.event_type == 'match');
        let m_trades = match_latest_trades.filter(t => t.event_id == data.item.match_id && t.event_type == 'match');
        if(m_trades.length == 0){
            m_trades = match_trades.filter(t => t.event_id == data.item.match_id && t.event_type == 'match');
        }
        let m_order_stats = match_order_stats.filter(os => os.event_id == data.item.match_id && os.event_type == 'match');
        return (
            <View key={`${tournament.tournament_id}_${data.item.match_id}`} style={{ margin:4 }}>
                <MatchMarket
                    match={data.item}
                    league={league}
                    float
                    disabled_markets={disabled_markets}
                    style={{ flexGrow:1 }}
                    away_athlete={away_athlete}
                    home_athlete={home_athlete}
                    highlight_router_orders={highlight_router_orders}
                    away_team={away_team}
                    show_grades={show_grades}
                    width={wrap_markets ? market_width : undefined}
                    home_team={home_team}
                    hide_liquidity={hide_match_liquidity ? true : false}
                    market={active_market}
                    latest_trades={m_trades}
                    best_available_orders={m_available_orders}
                    event_order_stats={m_order_stats}
                    onOrder={onOrder}
                    onTradeLongPress={onTradeLongPress}
                    onView={onView}
                    default_price_view={default_price_view}

                />
            </View>
        )
    }

    const renderTeams = (data:{ item:TeamProps, index:number }) => {
        if(!active_market){ return <></> }
        let team_available_orders = best_available_orders.filter(ba => ba.side_type == 'team' && ba.side_id == data.item.team_id);
        let team_trades = latest_trades.filter(t => t.side_type == 'team' && t.side_id == data.item.team_id);
        let team_order_stats = event_order_stats.filter(os => os.side_type == 'team' && os.side_id == data.item.team_id);
        return (
            <View key={`${tournament.tournament_id}_${data.item.team_id.toString()}`} style={{ margin:4 }}>
                <TeamTournamentMarket
                    team={data.item}
                    event_type='tournament'
                    onTradeLongPress={onTradeLongPress}
                    onView={onView}
                    width={wrap_markets ? market_width : undefined}
                    show_grades={show_grades}
                    float
                    highlight_router_orders={highlight_router_orders}
                    disabled_markets={disabled_markets}
                    hide_liquidity={hide_participant_liquidity ? true : false}
                    tournament={tournament}
                    event_order_stats={team_order_stats}
                    best_available_orders={team_available_orders}
                    latest_trades={team_trades}
                    default_price_view={default_price_view}
                    market={active_market}
                    onOrder={onOrder}
                />
            </View>
        )
    }


    const renderFlashHeaders = (data:{ item:PollProps, index:number }) => {
        const active = upcoming_flash_market?.poll_id == data.item.poll_id ? true : false
        return (
            <Button
                float={active ? true : false}
                style={{ padding:0, opacity:active?1:0.5, justifyContent:'center', alignItems:'center', width:40, height:40, borderRadius:0, borderTopRightRadius:8, borderTopLeftRadius:8, borderWidth:1, borderColor: C.borders.light }}
                onPress={() => setTournamentData({ ...tournament_data, selected_flash: data.item.poll_id })}
            >
                {data.item.poll_image ?
                <Image
                    source={{ uri: data.item.poll_image.url }}
                    style={{ height:40, width:40 }}
                    resizeMode='contain'
                />
                :
                <Icons.FlameIcon size={24} color={C.text.error} />
                }
            </Button>
        )
    }

    const renderAthletes = (data:{ item:AthleteProps, index:number }) => {
        if(!active_market){ return <></> }
        let team_available_orders = best_available_orders.filter(ba => ba.side_type == 'athlete' && ba.side_id == data.item.athlete_id);
        let team_trades = latest_trades.filter(t => t.side_type == 'athlete' && t.side_id == data.item.athlete_id);
        let team_order_stats = event_order_stats.filter(os => os.side_type == 'athlete' && os.side_id == data.item.athlete_id);
        return (
            <View key={`${tournament.tournament_id}_${data.item.athlete_id.toString()}`} style={{ margin:4 }}>
                <AthleteTournamentMarket
                    athlete={data.item}
                    event_type='tournament'
                    onTradeLongPress={onTradeLongPress}
                    onView={onView}
                    float
                    highlight_router_orders={highlight_router_orders}
                    league={league}
                    disabled_markets={disabled_markets}
                    style={{ flexGrow:1 }}
                    show_grades={show_grades}
                    hide_liquidity={hide_participant_liquidity ? true : false}
                    tournament={tournament}
                    width={wrap_markets ? market_width : undefined}
                    event_order_stats={team_order_stats}
                    best_available_orders={team_available_orders}
                    latest_trades={team_trades}
                    default_price_view={default_price_view}
                    market={active_market}
                    onOrder={onOrder}
                />
            </View>
        )
    }

    return (
        <View float={float} style={[{ borderRadius:8 }, style]}>
            <View type='header' transparent nativeID='event_header_data' style={{ flexDirection:'row', alignItems:'center', flexWrap:'wrap', padding:10, borderBottomWidth:1, borderBottomColor:C.borders.light }}>
                {onActivate ?
                <Button
                    float
                    style={{ marginRight:5, opacity: activate_loading || tournament.status == 'closed' ? 0.5 : 1, backgroundColor:C.text.success, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                    disabled={activate_loading || tournament.status == 'closed'}
                    loading={activate_loading} 
                    
                    onPress={() => onActivate(tournament.tournament_id, 'tournament')}
                >
                    <Icons.RefreshIcon size={20} color={C.text.white} />
                </Button>
                :league ?
                <Image
                    source={{ uri: league.league_image }}
                    style={{ height:40, width: 40, marginRight:10 }}
                    resizeMode='cover'
                />
                :<></>}
                <View transparent style={{ flex:1 }}>
                    <Text theme='h2'>{tournament.tournament_name}</Text>
                    <Text style={{ marginTop:3 }} theme='description'>{moment(tournament.scheduled_datetime).format('MMM DD YYYY')}</Text>
                </View>
                {onShare ?
                <Button style={{ marginLeft:10 }} onPress={() => onShare(tournament)}>
                    <Icons.ShareIcon size={14} color={C.text.action} />
                </Button>
                :<></>}
                {onViewMarketStats ?
                <Button
                    float
                    style={{ backgroundColor:C.text.gold, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                    disabled={activate_loading}
                    loading={activate_loading} 
                    
                    onPress={() => onViewMarketStats()}
                >
                    <Icons.PieIcon size={16} color={C.text.white} />
                </Button>
                :<></>}
            </View>
            {loading || !loaded ?
            <View transparent style={{ padding:20 }}>
                <ActivityIndicator size={'large'} color={C.text.h1} />
            </View>
            :
            <View type='body' transparent style={{ flexGrow:1 }}>
            {market_selector_location == 'top_right' ?
                <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10, zIndex:1 }}>
                    <View type='input' style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                        <View transparent style={{ padding:5, paddingLeft:10, paddingRight:10 }}>
                            <Icons.SearchIcon color={C.text.action} size={18} />
                        </View>
                        <TextInput
                            style={{ flex:1}}
                            transparent
                            placeholder='Search athletes, teams, matches ...'
                            onChangeText={(text) => setSearchValue(text)}
                        />
                    </View>
                    <Button float style={{ flexDirection:'row', alignItems:'center', marginLeft:5 }} onPress={() => setShowMarkets(!show_markets)}>
                        <Text style={{ marginRight:10 }} size={14} color={C.text.h1}>{active_market?.type == 'Stat' ? active_market.stat_label : active_market?.type}</Text>
                        <Icons.ChevronIcon color={C.text.h1} size={14} />
                    </Button>
                    {show_markets ?
                    <View transparent float style={{ position:'absolute', top:5, right:5, minWidth:175 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:5, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h2'>Select Market</Text>
                            </View>
                            <Button
                                title='X'
                                title_color={C.text.action}
                                style={{ borderRadius:100, padding:5, paddingLeft:10, paddingRight:10 }}
                                type='close'
                                onPress={() => setShowMarkets(false)}
                            />
                            
                        </View>
                        <View transparent style={{ padding:10 }}>
                            <FlatList
                                data={supported_markets}
                                renderItem={renderMarkets}
                                keyExtractor={(item) => item.market_id.toString()}
                            />
                        </View>
                    </View>
                    :<></>}
                </View>
            :<></>}
            <View style={{ flexDirection:'row', alignItems:'center' }}>
                <View style={{flex:1}}>
                    <LinearDiagnal label_size={14} label={active_type == 'futures' ? 'FUTURES' : 'MATCHES'} left_color={C.text.warning} right_color={C.text.error}/>
                </View>
                {((active_type == 'matches' && show_futures) || (active_type == 'futures' && show_matches)) ?
                <Button
                    title={`SEE ${active_type == 'futures' ? 'MATCHES' : 'FUTURES'}`}
                    type='close'
                    style={{ padding:10 }}
                    onPress={() => {
                        let new_active_market = active_market
                        if(active_type == 'futures'){
                            //need to find the match markets
                            new_active_market = all_supported_markets.find(sm => sm.event_type == 'match');
                        } else {
                            new_active_market = all_supported_markets.find(sm => sm.event_type == 'tournament');
                        }
                        setTournamentData({ ...tournament_data, active_type: active_type == 'futures' ? 'matches': 'futures', active_market: new_active_market })
                    }}
                />
                :<></>}
            </View>
            {active_type == 'futures' && show_teams && teams.length > 0 ?
            <View transparent style={{ padding:10 }}>
                {wrap_markets ?
                <View transparent style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                    {sorted_teams.map((t,i) => {
                        return renderTeams({ item:t, index:i })
                    })}
                </View>
                :
                <FlatList
                    data={sorted_teams}
                    key={`${tournament.tournament_id}_teams`}
                    initialNumToRender={5}
                    removeClippedSubviews
                    maxToRenderPerBatch={5}
                    windowSize={4}
                    renderItem={renderTeams}
                    horizontal
                    keyExtractor={(item) => item.team_id.toString()}
                />
                }
            </View>
            :<></>}
            {active_type == 'futures' && show_athletes && athletes.length > 0 ?
            <View transparent style={{ padding:10 }}>
                {wrap_markets ?
                <View transparent style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                    {sorted_athletes.map((a,i) => {
                        return renderAthletes({ item:a, index:i })
                    })}
                </View>
                :
                <FlatList
                    data={sorted_athletes}
                    renderItem={renderAthletes}
                    horizontal
                    initialNumToRender={5}
                    removeClippedSubviews
                    maxToRenderPerBatch={5}
                    windowSize={4}
                    keyExtractor={(item) => item.athlete_id.toString()}
                />
                }
            </View>
            :active_type == 'matches' && show_matches && matches.length > 0 ?
            <View transparent style={{ padding:10 }}>
                {wrap_markets ?
                <View transparent style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                    {sorted_matches.map((m,i) => {
                        return renderMatches({ item:m, index:i })
                    })}
                </View>
                :
                <FlatList
                    data={sorted_matches}
                    renderItem={renderMatches}
                    keyExtractor={(item) => item.match_id.toString()}
                    horizontal
                    initialNumToRender={5}
                    removeClippedSubviews
                    maxToRenderPerBatch={5}
                    windowSize={4}
                />
                }
            </View>
            :<></>}
            </View>
            }
            {flash_markets && onFlashSelect && flash_markets.length > 1 ?
            <View style={{ borderTopWidth:1, borderColor:C.borders.light, paddingTop:5 }}>
                <FlatList
                    data={flash_markets.sort((a,b) => (a.total_responses??0) - (b.total_responses??0) || moment(a.end_datetime).unix() - moment(b.end_datetime).unix())}
                    key='flash_markets_event'
                    horizontal
                    keyExtractor={(item) => item.poll_id.toString()}
                    renderItem={renderFlashHeaders}
                />
            </View>
            :<></>}
            {onFlashSelect && upcoming_flash_market ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:C.borders.light }}>
                {upcoming_flash_market.poll_image && flash_markets && flash_markets.length == 1 ?
                <Image
                    source={{ uri: upcoming_flash_market.poll_image.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode='cover'
                />
                : flash_markets && flash_markets.length == 1 ?
                <Icons.FlameIcon size={24} color={C.text.error} />
                :<></>}
                <View transparent style={{ flex:1, marginLeft:flash_markets && flash_markets.length == 1 ? 10: 0 , marginRight:10 }}>
                    <Text theme='h2'>{upcoming_flash_market.poll_question}</Text>
                </View>
                <Button
                    title='BUY NOW'
                    type='success'
                    style={{ padding:10 }}
                    onPress={() => onFlashSelect(upcoming_flash_market.poll_id)}
                />
            </View>
            :<></>}
            <View type='footer' style={{  flexDirection:'row', alignItems:'center', padding:10, borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                
                {!hide_liquidity ?
                <Button transparent style={{ flexGrow:1, flexDirection:'row', padding:10 }} onPress={() => {
                    if(!active_market){ return }
                    onViewAdditionalMarkets(tournament.tournament_id, 'tournament');
                }}>
                    <View float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
                :<View transparent style={{flex:1}} />}
                {!market_selector_location ?
                <View transparent>
                <Button float style={{ flexDirection:'row', alignItems:'center', marginLeft:5 }} onPress={() => setShowMarkets(!show_markets)}>
                        <Text style={{ marginRight:10 }} size={14} color={C.text.h1}>{active_market?.type == 'Stat' ? active_market.stat_label : active_market?.type}</Text>
                        <Icons.ChevronIcon color={C.text.h1} size={14} />
                    </Button>
                    {show_markets ?
                    <View transparent float style={{ position:'absolute', bottom:5, right:5, minWidth:175 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:5, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h2'>Select Market</Text>
                            </View>
                            <Button
                                title='X'
                                title_color={C.text.action}
                                style={{ borderRadius:100, padding:5, paddingLeft:10, paddingRight:10 }}
                                type='close'
                                onPress={() => setShowMarkets(false)}
                            />
                            
                        </View>
                        <View transparent style={{ padding:10 }}>
                            <FlatList
                                data={supported_markets}
                                renderItem={renderMarkets}
                                keyExtractor={(item) => item.market_id.toString()}
                            />
                        </View>
                    </View>
                    :<></>}
                </View>
                :<></>}
            </View>
        </View>
    )
}

export default TournamentMarket